import React from 'react'
import Svg, { Circle, Line, Path, Rect } from 'react-native-svg'

import { colors } from '../../tokens'

/**
 * Shared cross-platform outline/solid glyphs for the redesigned primitives
 * (AOS-598). Rendered **directly** with an explicit `color` (applied as
 * `stroke` for outlines, `fill` for dots) so they paint identically on web and
 * React Native — the same approach the picker glyphs use (see
 * `../picker-icons/pickerGlyphs.tsx` for the why). Internal only.
 */

export const GLYPH_COLORS = {
    default: colors.content.default,
    muted: colors.content.muted,
    onPrimary: colors.content['on-primary'],
    accent: colors.action.default,
    link: colors.content.link,
}

export type GlyphTone = keyof typeof GLYPH_COLORS

export type GlyphProps = {
    /** Square size in px. */
    size?: number
    /** Glyph colour (hex). Defaults to the standard text colour. */
    color?: string
}

const STROKE = {
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
}

const resolve = (color?: string): string => color ?? GLYPH_COLORS.default

export const PlusGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => {
    const stroke = resolve(color)
    return (
        <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
            <Line x1={12} y1={5} x2={12} y2={19} stroke={stroke} {...STROKE} />
            <Line x1={5} y1={12} x2={19} y2={12} stroke={stroke} {...STROKE} />
        </Svg>
    )
}

export const MinusGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Line x1={5} y1={12} x2={19} y2={12} stroke={resolve(color)} {...STROKE} />
    </Svg>
)

/** Tall thin chevron separating the legs of a list row (towing list). `size` is
 * the height; the width keeps the 18:40 ratio so the 2px stroke never scales. */
export const ThinChevronRightGlyph: React.FC<GlyphProps> = ({ size = 40, color }) => (
    <Svg width={(size * 18) / 40} height={size} viewBox='0 0 18 40' fill='none'>
        <Path d='M2 1.5L16 20.5L2 39' stroke={resolve(color)} {...STROKE} />
    </Svg>
)

export type BaggageDeliveryFill = 'full' | 'half' | 'empty'

/** Baggage briefcase glyph (full / half / empty fill), tinted by `color` — the
 * baggage-help delivery-state icon (matches the flight-table `BaggageIndicatorIcon`
 * silhouette: rectangular body + handle + lid). Distinct from the stroke-only
 * outline `BaggageGlyph` (flight-table cell). */
export const CloseGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path
            fill={resolve(color)}
            fillRule='evenodd'
            clipRule='evenodd'
            d='M18.984 6.28197L13.416 11.85L18.984 17.418L17.568 18.834L12 13.266L6.43199 18.834L5.01599 17.418L10.584 11.85L5.01599 6.28197L6.43199 4.86597L12 10.434L17.568 4.86597L18.984 6.28197Z'
        />
    </Svg>
)

export const CheckGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path d='M20 6L9 17l-5-5' stroke={resolve(color)} {...STROKE} />
    </Svg>
)

// Check inside a circle outline (Figma towing stand "confirmed" marker,
// node 10423:204798) — both circle and tick stroked in the given colour.
export const CheckCircleGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => {
    const stroke = resolve(color)
    return (
        <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
            <Circle cx={12} cy={12} r={9} stroke={stroke} strokeWidth={2} />
            <Path d='M8.5 12.5l2.5 2.5 4.5-5.5' stroke={stroke} {...STROKE} />
        </Svg>
    )
}

// Figma Navigation/ChevronDown (46:565) — same reason as CloseGlyph above.
export const ChevronDownGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path
            fill={resolve(color)}
            fillRule='evenodd'
            clipRule='evenodd'
            d='M8.19693 9.10099L12.3989 13.325L16.6009 9.10099L17.8989 10.399L12.3989 15.899L6.89893 10.399L8.19693 9.10099Z'
        />
    </Svg>
)

// Outline alert triangle with an exclamation mark — the restriction card icon
// (Figma Stands&Gates restriction details). Stroke-based like the other outline glyphs.
export const WarningTriangleGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => {
    const stroke = resolve(color)
    return (
        <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
            <Path
                d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z'
                stroke={stroke}
                {...STROKE}
            />
            <Line x1={12} y1={9} x2={12} y2={13} stroke={stroke} {...STROKE} />
            <Line x1={12} y1={17} x2={12.01} y2={17} stroke={stroke} {...STROKE} />
        </Svg>
    )
}

export const ErrorCircleGlyph: React.FC<GlyphProps> = ({ size = 16, color }) => {
    const stroke = color ?? colors.status.critical
    return (
        <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
            <Circle cx={12} cy={12} r={9} stroke={stroke} strokeWidth={2} />
            <Path d='M9 9l6 6M15 9l-6 6' stroke={stroke} strokeWidth={2} strokeLinecap='round' />
        </Svg>
    )
}

export const MoreVerticalGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => {
    const fill = resolve(color)
    return (
        <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
            <Circle cx={12} cy={5} r={1.6} fill={fill} />
            <Circle cx={12} cy={12} r={1.6} fill={fill} />
            <Circle cx={12} cy={19} r={1.6} fill={fill} />
        </Svg>
    )
}

// Solid sort triangles (Figma table header `sort=up`/`sort=down`). The ascending
// arrow points up, descending points down — drawn filled so they read at the
// small 16px header size.
export const TriangleUpGlyph: React.FC<GlyphProps> = ({ size = 16, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path d='M12 8l5 8H7z' fill={resolve(color)} />
    </Svg>
)

export const TriangleDownGlyph: React.FC<GlyphProps> = ({ size = 16, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path d='M7 8h10l-5 8z' fill={resolve(color)} />
    </Svg>
)

// Pencil / edit outline (Unit Task List note box edit affordance, AOS-667).
export const PencilGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => {
    const stroke = resolve(color)
    return (
        <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
            <Path d='M12 20h9' stroke={stroke} {...STROKE} />
            <Path
                d='M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z'
                stroke={stroke}
                {...STROKE}
            />
        </Svg>
    )
}

// Suitcase outline (flight-table baggage cell). Handle + body with a centre seam.
export const BaggageGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => {
    const stroke = resolve(color)
    return (
        <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
            <Path d='M9 7V6a3 3 0 0 1 6 0v1' stroke={stroke} {...STROKE} />
            <Rect x={4} y={7} width={16} height={13} rx={2} stroke={stroke} {...STROKE} />
            <Line x1={12} y1={7} x2={12} y2={20} stroke={stroke} {...STROKE} />
        </Svg>
    )
}

export const EventExportGlyph: React.FC<GlyphProps> = ({ size = 100, color }) => {
    const fill = resolve(color)
    return (
        <Svg width={size} height={size} viewBox='0 0 100 100' fill='none'>
            <Path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M50 98C76.51 98 98 76.51 98 50C98 23.49 76.51 2 50 2C23.49 2 2 23.49 2 50C2 76.51 23.49 98 50 98ZM50 100C22.386 100 0 77.614 0 50C0 22.386 22.386 0 50 0C77.614 0 100 22.386 100 50C100 77.614 77.614 100 50 100Z'
                fill={fill}
            />
            <Path
                d='M69.537 55.353C71.7723 57.5883 72.89 60.286 72.89 63.446C72.89 68.186 71.773 69.302 69.537 71.538C67.3017 73.7733 64.6043 74.891 61.445 74.891C58.2857 74.891 55.5883 73.7733 53.353 71.538C51.1177 69.302 50 66.6047 50 63.446C50 60.286 51.1177 57.5883 53.353 55.353C55.5883 53.1177 58.2857 52 61.445 52C64.605 52 67.3023 53.1177 69.537 55.353ZM68.083 70.127C69.9317 68.2783 70.856 66.0517 70.856 63.447C70.856 60.8423 69.9387 58.6227 68.104 56.788C66.2693 54.9533 64.0497 54.036 61.445 54.036C58.8403 54.036 56.6207 54.9533 54.786 56.788C52.9513 58.6227 52.034 60.8423 52.034 63.447C52.034 66.0517 52.9513 68.2783 54.786 70.127C56.6207 71.9757 58.8403 72.9 61.445 72.9C64.0497 72.9 66.2623 71.9757 68.083 70.127Z'
                fill={fill}
            />
            <Path
                d='M64.9802 66.5653C65.1672 66.7993 65.2633 67.0753 65.2633 67.3753C65.2633 67.6753 65.1673 67.9523 64.9263 68.2433C64.3863 68.7373 63.7483 68.7373 63.2363 68.2253L60.5543 65.5023H60.5133L60.3633 65.3522C60.2468 65.2389 60.1546 65.1031 60.0922 64.953C60.0298 64.8029 59.9985 64.6418 60.0003 64.4792V58.1943C59.997 58.0366 60.0256 57.8798 60.0845 57.7335C60.1433 57.5871 60.2312 57.4542 60.3427 57.3427C60.4542 57.2312 60.5871 57.1433 60.7335 57.0845C60.8798 57.0256 61.0366 56.997 61.1943 57.0003C61.5243 57.0003 61.8193 57.1153 62.0463 57.3423C62.2733 57.5693 62.3883 57.8623 62.3883 58.1943V64.0092L64.9802 66.5653Z'
                fill={fill}
            />
            <Path
                d='M51.48 27L63.388 38.908V49.398H61.031V42.2H48.368V29.382H38.381V62.724H47.605V65.105H38.381C37.7357 65.105 37.1777 64.8693 36.707 64.398C36.235 63.9267 35.9993 63.3687 36 62.724V29.382C36 28.7367 36.2357 28.1783 36.707 27.707C37.1777 27.2357 37.7357 27 38.381 27H51.48ZM60.927 40.068L50.511 29.382H50.289V40.295H60.927V40.068Z'
                fill={fill}
            />
        </Svg>
    )
}
